import {
    Button,
    Card,
    Header,
    HStack,
    IconHelp,
    IconRefresh,
    Image,
    Link,
    Switcher,
    Text,
    VStack,
} from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { getSettingLoyaltyProgramUrl } from '@/entities/merchSetting';
import { type SchemaOrderLoyaltyInfoRegisteredUser } from '@/shared/api/openApi';
import goldenRubleBagSrc from '@/shared/assets/GoldenRubleBag.png';
import { formatBonusWithCount } from '@/shared/lib/bonus';
import { formatPrice } from '@/shared/lib/formatPrice';
import { getRequestContext } from '@/shared/lib/requestContext';
import IconInfo from '../../assets/IconInfo.react.svg';
import { LoyaltyTooltip } from '../LoyaltyTooltip';
import styles from './LoyaltySwitcher.module.css';

const TOOLTIP_TITLE = 'Ваши бонусы';

const TooltipSubtitle = () => {
    const {
        company: { companyShopUrl },
    } = getRequestContext();

    const loyaltyProgramUrl = getSettingLoyaltyProgramUrl(companyShopUrl);

    return (
        <>
            Вы получаете их за покупки и другие события. Бонусами можно оплатить часть заказа.{' '}
            <Link href={loyaltyProgramUrl} target="_blank">
                Подробнее здесь
            </Link>
        </>
    );
};

export interface LoyaltySwitcherProps extends TestProps {
    loyalty: SchemaOrderLoyaltyInfoRegisteredUser;
    isRefreshing: boolean;
    onRefresh: () => Promise<unknown>;
    isSelected: boolean;
    onChange: (value: boolean) => void;
}

const SwitchButton = (props: Omit<LoyaltySwitcherProps, 'onRefresh' | 'isRefreshing'>) => {
    const { loyalty, isSelected, onChange } = props;

    const bonuses = formatPrice(loyalty.bonuses_can_charge, undefined, { style: 'decimal' });

    return loyalty.bonuses_can_charge === 0 && loyalty.user_bonus_balance > 0 ? (
        <Text role="supplementary" size="s" className={styles.subtitle}>
            <HStack gap="4" alignItems="center" justifyContent="center">
                Нельзя списать
                <LoyaltyTooltip<SVGSVGElement>
                    subtitle="На товары в заказе не действует программа лояльности"
                    tooltipProps={{
                        placement: 'top',
                        color: 'white',
                        maxWidth: '240',
                    }}
                >
                    {({ openTooltip, ref }) => <IconHelp ref={ref} onClick={openTooltip} cursor="pointer" />}
                </LoyaltyTooltip>
            </HStack>
        </Text>
    ) : (
        <HStack gap="12" alignItems="center">
            {loyalty.bonuses_can_charge > 0 && <Text size="mc">{bonuses}</Text>}

            <Switcher
                className={styles.switcher}
                isSelected={isSelected}
                isDisabled={loyalty.user_bonus_balance === 0 || loyalty.bonuses_can_charge === 0}
                aria-label="Списать бонусы"
                onChange={onChange}
            />
        </HStack>
    );
};

export const LoyaltySwitcher = (props: LoyaltySwitcherProps) => {
    const { loyalty, isSelected, isRefreshing, onRefresh, onChange } = props;

    const pluralizedBonuses = formatBonusWithCount(loyalty.user_bonus_balance);

    return (
        <Card className={styles.card} color="white" borderRadius="20" padding="20">
            <HStack gap="12" alignItems="center">
                <Image src={goldenRubleBagSrc} width="28" height="28" alt="Списать бонусы" />

                <VStack className={styles.texts}>
                    <Header level="h4" className={styles.title}>
                        <HStack gap="4" alignItems="center">
                            Списать бонусы
                            <LoyaltyTooltip<HTMLAnchorElement>
                                title={TOOLTIP_TITLE}
                                subtitle={<TooltipSubtitle />}
                                tooltipProps={{
                                    placement: 'top',
                                    color: 'white',
                                    maxWidth: '360',
                                }}
                            >
                                {({ openTooltip, ref }) => (
                                    <Link ref={ref}>
                                        <IconInfo onClick={openTooltip} />
                                    </Link>
                                )}
                            </LoyaltyTooltip>
                        </HStack>
                    </Header>

                    {loyalty.error_during_refetch ? (
                        <Text role="supplementary" size="s" className={styles.subtitle}>
                            Не удалось загрузить данные
                        </Text>
                    ) : (
                        <Text role="supplementary" size="s" className={styles.subtitle}>
                            Баланс: {pluralizedBonuses}
                        </Text>
                    )}
                </VStack>

                {loyalty.error_during_refetch ? (
                    <Button color="contrast" size="s" isDisabled={isRefreshing} onClick={onRefresh}>
                        <HStack gap="12" alignItems="center">
                            <Text size="mc">Обновить</Text>

                            <IconRefresh />
                        </HStack>
                    </Button>
                ) : (
                    <SwitchButton loyalty={loyalty} isSelected={isSelected} onChange={onChange} />
                )}
            </HStack>
        </Card>
    );
};
